import fs from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";

const PROMPT_FILENAME = "appostle-system-prompt.md";

let cachedPrompt: string | null = null;

function loadPromptFromDisk(): string {
  const here = path.dirname(fileURLToPath(import.meta.url));
  const candidates = [
    path.join(here, PROMPT_FILENAME),
    path.join(process.cwd(), "packages", "server", "src", "server", "agent", PROMPT_FILENAME),
  ];

  for (const candidate of candidates) {
    try {
      return fs.readFileSync(candidate, "utf8").trim();
    } catch {
      // Try the next known runtime layout.
    }
  }

  throw new Error(`Unable to locate ${PROMPT_FILENAME}`);
}

export function getAppostleSystemPrompt(): string {
  cachedPrompt ??= loadPromptFromDisk();
  return cachedPrompt;
}

export function composeAppostleSystemPrompt(sessionSystemPrompt?: string | null): string {
  return [getAppostleSystemPrompt(), sessionSystemPrompt?.trim()]
    .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
    .join("\n\n");
}

const RUNTIME_CONTEXT_START = "<appostle-runtime-context>";
const RUNTIME_CONTEXT_END = "</appostle-runtime-context>";

export interface AppostleRuntimeMachine {
  serverId: string;
  name: string;
  online: boolean;
  organization?: { name?: string | null; slug?: string | null } | null;
  owner?: { userId?: string | null; handle?: string | null } | null;
}

export function stripAppostleRuntimeContext(prompt?: string | null): string | undefined {
  if (!prompt) return undefined;
  const escapedStart = RUNTIME_CONTEXT_START.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const escapedEnd = RUNTIME_CONTEXT_END.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`\\n*${escapedStart}[\\s\\S]*?${escapedEnd}\\n*`, "g");
  const stripped = prompt.replace(pattern, "\n\n").trim();
  return stripped.length > 0 ? stripped : undefined;
}

export function buildAppostleRuntimeMachineContext(
  machines: AppostleRuntimeMachine[],
): string | null {
  const visibleMachines = machines
    .filter((machine) => machine.name.trim().length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  if (visibleMachines.length === 0) return null;

  const rows = visibleMachines.map((machine) => {
    const details = [
      `serverId: ${machine.serverId}`,
      `status: ${machine.online ? "online" : "offline"}`,
      machine.organization?.name ? `org: ${machine.organization.name}` : null,
      machine.organization?.slug ? `orgSlug: ${machine.organization.slug}` : null,
      machine.owner?.handle ? `owner: ${machine.owner.handle}` : null,
      machine.owner?.userId ? `ownerUserId: ${machine.owner.userId}` : null,
    ].filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
    return `- ${machine.name} (${details.join(", ")})`;
  });

  return [
    RUNTIME_CONTEXT_START,
    "## Appostle Machine Directory",
    "",
    "These are the machines this Appostle account can see right now:",
    ...rows,
    "",
    "When the user asks about one of these machines by name, nickname, owner, organization, or a clear natural-language alias, use the Appostle cross-host tools instead of local shell commands.",
    "Resolve host references case-insensitively and tolerate punctuation, spaces, and hyphen differences, for example `Nico's MacBook` can match `Nicos-MacBook-Air`.",
    "If exactly one machine is the clear match, pass that machine name or serverId to the tool. If several machines match equally, ask a short clarifying question.",
    "For filesystem inspection requests on another machine, infer the shell command from the user's intent, for example Desktop listing -> `ls -la ~/Desktop`.",
    "Do not answer a request about a listed sibling machine by running local Bash on the current machine.",
    RUNTIME_CONTEXT_END,
  ].join("\n");
}

export function appendAppostleRuntimeContext(
  sessionSystemPrompt: string | null | undefined,
  runtimeContext: string | null | undefined,
): string | undefined {
  const stripped = stripAppostleRuntimeContext(sessionSystemPrompt);
  if (!runtimeContext?.trim()) return stripped;
  return [stripped, runtimeContext.trim()]
    .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
    .join("\n\n");
}
